import React from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Info } from 'lucide-react';
import { TNaerasDashboardOutflows, TNaerasPanel } from '@/data/types';
import PanelCard, { Kpi } from './PanelCard';
import { COLORS, isNum, mwh, num, shortDate } from '../utils/format';

interface OutflowsPanelProps {
  panel: TNaerasPanel<TNaerasDashboardOutflows> | undefined;
  /** "1/10–15/4", from `/naeras/settings`. */
  seasonWindow: string;
}

interface Column {
  name: string;
  bottom: number | null;
  top: number | null;
  bottomLabel: string;
  topLabel: string;
}

const ColumnTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;

  const column: Column = payload[0].payload;

  return (
    <div className="rounded-2xl border border-slate-100 bg-white/95 p-3.5 shadow-xl backdrop-blur-md">
      <p className="mb-2 text-xs font-bold text-slate-800">{label}</p>
      {[
        { label: column.topLabel, value: column.top, color: COLORS.hydro },
        { label: column.bottomLabel, value: column.bottom, color: COLORS.hybrid },
      ].map((row) => (
        <div key={row.label} className="flex items-center gap-2 py-0.5 text-xs">
          <span className="h-2 w-2 rounded-full" style={{ backgroundColor: row.color }} />
          <span className="font-medium text-slate-600">{row.label}:</span>
          <span className="font-extrabold text-slate-900">{mwh(row.value)}</span>
        </div>
      ))}
    </div>
  );
};

/**
 * §2.2 — yesterday's production: what was offered, how it splits into hybrid
 * and hydro, and what SCADA measured. Fixed for the day once Job A has run.
 *
 * The season is the one the day was bid with (`is_winter`): a day inside the
 * wet window is still dry when the switch was closed, so the window is only
 * the label's reference, never the source.
 */
export const OutflowsPanel: React.FC<OutflowsPanelProps> = ({ panel, seasonWindow }) => (
  <PanelCard
    title="Εκροές Χθες"
    subtitle={
      panel?.available
        ? `${shortDate(panel.reconciled_date)} — ${
            panel.is_winter ? `Υγρή περίοδος (${seasonWindow})` : 'Ξηρή περίοδος'
          }`
        : 'Υβριδικές και υδροηλεκτρικές εκροές της χθεσινής ημέρας'
    }
    panel={panel}
    delay={0.1}
    aside={(data) => (
      <div className="grid grid-cols-2 gap-3">
        <Kpi label="Υβριδικές εκροές" value={mwh(data.outflows.hybrid_mwh)} tone="hybrid" />
        <Kpi label="Υδροηλεκτρικές εκροές" value={mwh(data.outflows.hydro_mwh)} tone="hydro" />
      </div>
    )}
  >
    {(data) => {
      const middleLabel =
        data.rule === 'trust_switch' ? 'Εκτίμηση πραγματικών εκροών' : 'Υβριδικές εκροές';

      const columns: Column[] = [
        {
          name: 'Προσφορές',
          bottom: data.offers.hybrid_mwh,
          top: data.offers.hydro_mwh,
          bottomLabel: 'Υβριδική',
          topLabel: 'Υδροηλεκτρική',
        },
        {
          name: middleLabel,
          bottom: data.outflows.hybrid_mwh,
          top: data.outflows.hydro_mwh,
          bottomLabel: 'Υβριδική',
          topLabel: 'Υδροηλεκτρική',
        },
        {
          name: 'Μετρήσεις SCADA',
          bottom: data.scada.lower_mwh,
          top: data.scada.upper_mwh,
          bottomLabel: 'Κάτω Προεσπέρα',
          topLabel: 'Προεσπέρα',
        },
      ];

      // Dry season: no hydro, so the outflows column and the Κάτω Προεσπέρα
      // block are the same energy — a line between their tops says so.
      const outflowTop = (data.outflows.hybrid_mwh ?? 0) + (data.outflows.hydro_mwh ?? 0);
      const showEqualityLine = !data.is_winter && isNum(data.outflows.hybrid_mwh);

      return (
        <>
          <div className="h-72 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={columns} margin={{ top: 20, right: 20, left: 0, bottom: 5 }} barSize={72}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                <XAxis
                  dataKey="name"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: '#64748b', fontSize: 12, fontWeight: 700 }}
                  dy={6}
                />
                <YAxis
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: '#94a3b8', fontSize: 11, fontWeight: 600 }}
                  tickFormatter={(v) => num(v, 0)}
                  unit=" MWh"
                  width={72}
                />
                <Tooltip content={<ColumnTooltip />} cursor={{ fill: 'rgba(241, 245, 249, 0.5)' }} />
                <Bar dataKey="bottom" stackId="stack" fill={COLORS.hybrid} />
                <Bar dataKey="top" stackId="stack" fill={COLORS.hydro} radius={[8, 8, 0, 0]} />
                {showEqualityLine && (
                  <ReferenceLine
                    segment={[
                      { x: middleLabel, y: outflowTop },
                      { x: 'Μετρήσεις SCADA', y: outflowTop },
                    ]}
                    stroke={COLORS.neutral}
                    strokeDasharray="4 3"
                  />
                )}
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-4 text-xs font-semibold text-gray-600">
            <span className="flex items-center gap-1.5">
              <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: COLORS.hybrid }} />
              Υβριδική / Κάτω Προεσπέρα
            </span>
            <span className="flex items-center gap-1.5">
              <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: COLORS.hydro }} />
              Υδροηλεκτρική / Προεσπέρα
            </span>
          </div>

          <div className="mt-4 flex items-center gap-2.5 rounded-2xl border border-amber-200/80 bg-amber-50/80 p-3.5 text-xs text-amber-900">
            <Info className="h-4 w-4 shrink-0 text-amber-600" />
            <p>
              <strong>Σημείωση:</strong> Η ενέργεια της Κάτω Προεσπέρας περιέχει υδροηλεκτρική και υβριδική
              ενέργεια.
            </p>
          </div>
        </>
      );
    }}
  </PanelCard>
);

export default OutflowsPanel;
